// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import colors from '../colors';

const Fonts = {
  serif: 'Untitled Serif, serif',
  sans: 'Untitled Sans, sans-serif',
};

const FontWeights = {
  normal: 400,
  semiBold: 600,
};

const TextColors = {
  primary: colors.mediumEvergreen,
  secondary: colors.lightEvergreen,
  link: colors.darkGreen,
  linkHover: colors.mediumGreen,
};

const SpacingReset = `
  margin: 0;
  padding: 0;
`;

const PageHeader = styled.h1`
  font-family: ${Fonts.serif};
  font-weight: ${FontWeights.normal};
  font-size: 2.25rem;
  line-height: 3rem;
  color: ${TextColors.primary};
  ${SpacingReset}
`;
PageHeader.displayName = 'PageHeader';

const SectionHeader = styled.h2`
  font-family: ${Fonts.sans};
  font-weight: ${FontWeights.normal};
  font-size: 1.3125rem;
  line-height: 2rem;
  color: ${TextColors.primary};
  ${SpacingReset}
`;
SectionHeader.displayName = 'SectionHeader';

const SubSectionHeader = styled.h3`
  font-family: ${Fonts.sans};
  font-weight: ${FontWeights.semiBold};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${TextColors.secondary};
  ${SpacingReset}
`;
SubSectionHeader.displayName = 'SubSectionHeader';

const Body = styled.p`
  font-family: ${Fonts.sans};
  font-weight: ${FontWeights.normal};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${TextColors.primary};
  ${SpacingReset}
`;
Body.displayName = 'Body';

const SecondaryBody = styled.p`
  font-family: ${Fonts.sans};
  font-weight: ${FontWeights.normal};
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: ${TextColors.primary};
  ${SpacingReset}
`;
SecondaryBody.displayName = 'SecondaryBody';

const StyledAnchorTag = styled.a`
  font-family: ${Fonts.sans};
  font-weight: ${FontWeights.normal};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${TextColors.link};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${TextColors.linkHover};
  }

  &:focus {
    color: ${TextColors.linkHover};
  }
`;

type LinkProps = {
  href: string,
  children: Node,
  rel?: string,
  target?: string,
};

const Link = (props: LinkProps) => {
  return <StyledAnchorTag {...props}>{props.children}</StyledAnchorTag>;
};

const ExternalLink = (props: LinkProps) => {
  return (
    <StyledAnchorTag target="_blank" rel="noopener noreferrer" {...props}>
      {props.children}
    </StyledAnchorTag>
  );
};

export {
  PageHeader,
  SectionHeader,
  SubSectionHeader,
  Body,
  SecondaryBody,
  Link,
  ExternalLink,
};
